import { NameCell } from '@openx/components-core/lib/NameCell/NameCell';
import { StatusIndicator } from '@openx/components-core/lib/StatusIndicator/StatusIndicator';
import { ValuePlaceholder } from '@openx/components-core/lib/Value/ValuePlaceholder/ValuePlaceholder';
import { AdFormat } from '@openx/types/gqlTypes';
import type { Columns } from '@openx/types/tableHelpers';
import { toUTCDate } from '@openx/utils/lib/date';

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { AdvertiserOption } from '../../types';
import { getAdFormatLabels, getAdvertiserOptionsMap, getStatusLabels } from '../../utils';

import type { CreativeRow } from '../types';

type UseTableColumnsProps = {
  advertiserOptions: AdvertiserOption[];
  searchPhrase?: string;
};

export const useTableColumns = ({ advertiserOptions, searchPhrase }: UseTableColumnsProps): Columns<CreativeRow> => {
  const { t } = useTranslation();

  const columns = useMemo((): Columns<CreativeRow> => {
    const statusLabels = getStatusLabels(t);
    const adFormatLabels = getAdFormatLabels(t);
    const advertiserOptionsMap = getAdvertiserOptionsMap(advertiserOptions);

    return [
      {
        key: 'name',
        title: t('Name'),
        width: '30%',
        render: creative => (
          <NameCell
            name={creative.name}
            id={creative.crid}
            subRowLabel="CrID:"
            pathTo={`/aqua/creatives/${creative.id}`}
            searchPhrase={searchPhrase}
          />
        ),
      },
      {
        key: 'status',
        title: t('Status'),
        width: '12%',
        render: creative => <StatusIndicator status={creative.status} label={statusLabels[creative.status]} />,
      },
      {
        key: 'advertiser_domain',
        title: t('Advertiser'),
        width: '20%',
        render: creative => advertiserOptionsMap[creative.account_uid] ?? creative.account_uid,
      },
      {
        key: 'ad_format',
        title: t('Format'),
        width: '12%',
        render: creative => adFormatLabels[creative.ad_format],
      },
      {
        key: 'width',
        title: t('Size'),
        width: '10%',
        render: creative => {
          if (creative.ad_format === AdFormat.Video) {
            return <ValuePlaceholder />;
          }

          return creative.width && creative.height ? `${creative.width} × ${creative.height}` : '—';
        },
      },
      {
        key: 'created_date',
        title: t('Created'),
        width: '10%',
        render: creative => toUTCDate(creative.created_date),
      },
    ];
  }, [t, advertiserOptions, searchPhrase]);

  return columns;
};
